import BuildConfig from '$extensionSrc/buildConfig'
import { extensionDashboardUrl } from '$extensionSrc/consts/urlConsts'
import { clearActiveTabId } from '$extensionSrc/data/activeTabId'
import isRescan from '$extensionSrc/data/automations/isRescan'
import { setBatchUpdateRecommendationKeys } from '$extensionSrc/data/batchUpdateRecommendationKeys'
import {
  clearDeeplinkFeatureKey,
  updateDeeplinkFeatureKey,
} from '$extensionSrc/data/deeplinkFeatureKeys'
import {
  setStoredMultiPlatformMachineKey,
  setStoredMultiPlatformMachineOverlayStatus,
  setStoredMultiPlatformRecommendationKeys,
  setStoredMultiPlatformRemainingPlatforms,
  setStoredMultiPlatformTabId,
  getStoredMultiPlatformRemainingPlatforms,
  MULTI_PLATFORM_MACHINE_KEY_PREFIX,
  updateStoredMultiPlatformMachineOverlayStatusTitle,
  updateStoredMultiPlatformMachineOverlayPlatformStatus,
  getStoredMultiPlatformRecommendationKeys,
  updateStoredMultiPlatformMachineOverlayStatusKeyRunningToPost,
  getMultiPlatformRunningStatusKeyForAutomationType,
  saveCurrentAgenticSidebarState,
  getStoredAgenticSidebarState,
} from '$extensionSrc/data/multiPlatform'
import { setOverlayScrollPosition } from '$extensionSrc/data/overlay/scrollPosition'
import { getPlatformName } from '$extensionSrc/data/platform/name'
import { RecommendationKeyEnum } from '$extensionSrc/recommendations/recommendationTypes'
import * as canIUse from '$extensionSrc/utils/canIUse'
import {
  getPlatformLoginUrl,
  getPlatformUrl,
} from '$extensionSrc/utils/platformMetadataUtils'

import {
  AutomationSequenceDefs,
  AutomationStageMachineDef,
  PlatformAutomationMachineStorageKeys,
  PlatformMachineStorageKeysMap,
  UserContext,
  StageDef,
  StageStatus,
} from './automationMachineInterfaces'
import {
  throwIfGlobalInterruptRequested,
  resetGlobalInterruptRequest,
  GlobalInterrupt,
} from './globalInterrupt'
import { homeUrl as googleHomeUrl } from './google/googleUrls'
import { createStageMachine, machineCleanUp } from './machine'
import {
  Machine,
  MachineContext,
  Stage,
  StageRegistry,
  InputData,
  AdditionalMachineData,
  OnTriggeringEventFn,
  ResumeDataBlob,
  MultiPlatformMachinePlatformStatus,
  MultiPlatformMachinePlatformStatuses,
  MultiPlatformStatusKey,
} from './machineInterfaces'
import { NeedsUserInputError } from './needsInputError'
import {
  DEFAULT_ACTION_STAGE_TIMEOUT,
  NO_TIMEOUT,
  REDIRECT_RETRY_MAX,
} from '../consts/stageMachineConsts'
import * as state from '../state'
import * as communication from '../utils/communication'
import {
  AutomationType,
  ControlStageKey,
  MACHINE_STORAGE_KEY_SUFFIX,
  PLATFORM,
  STORAGE_KEY,
  StageType,
  TriggeringEventType,
} from '../utils/enums'
import * as localStorage from '../utils/localStorage'
import { _debug } from '../utils/logging'
import { logError, logInfo } from '../utils/loggingUtils'
import { throwIfTimeout } from '../utils/promises'
import { remoteLog } from '../utils/remoteLog'
import { FACEBOOK_PASSWORD_PROMPT_EXCEPTION_NAME } from './facebook/facebookPasswordPromptCheck'
import { FACEBOOK_RATE_LIMIT_EXCEPTION_NAME } from './facebook/facebookRateLimitCheck'
import { LinkedinShowProfilePhotoErrorName } from './linkedin/linkedinShowProfilePhotoError'

// Platform automation machine storage keys

const blueskyMachineStorageKeys: PlatformAutomationMachineStorageKeys = {
  machineKeyStorageKey: STORAGE_KEY.BLUESKY_MACHINE_KEY,
  machineOverlayStatusStorageKey: STORAGE_KEY.BLUESKY_MACHINE_OVERLAY_STATUS,
  machineContextStorageKey: STORAGE_KEY.BLUESKY_MACHINE_CONTEXT,
  machineSignalStorageKey: STORAGE_KEY.BLUESKY_MACHINE_SIGNAL,
}

const facebookMachineStorageKeys: PlatformAutomationMachineStorageKeys = {
  machineKeyStorageKey: STORAGE_KEY.FACEBOOK_MACHINE_KEY,
  machineOverlayStatusStorageKey: STORAGE_KEY.FACEBOOK_MACHINE_OVERLAY_STATUS,
  machineContextStorageKey: STORAGE_KEY.FACEBOOK_MACHINE_CONTEXT,
  machineSignalStorageKey: STORAGE_KEY.FACEBOOK_MACHINE_SIGNAL,
}

const googleMachineStorageKeys: PlatformAutomationMachineStorageKeys = {
  machineKeyStorageKey: STORAGE_KEY.GOOGLE_MACHINE_KEY,
  machineOverlayStatusStorageKey: STORAGE_KEY.GOOGLE_MACHINE_OVERLAY_STATUS,
  machineContextStorageKey: STORAGE_KEY.GOOGLE_MACHINE_CONTEXT,
  machineSignalStorageKey: STORAGE_KEY.GOOGLE_MACHINE_SIGNAL,
}

const instagramMachineStorageKeys: PlatformAutomationMachineStorageKeys = {
  machineKeyStorageKey: STORAGE_KEY.INSTAGRAM_MACHINE_KEY,
  machineOverlayStatusStorageKey: STORAGE_KEY.INSTAGRAM_MACHINE_OVERLAY_STATUS,
  machineContextStorageKey: STORAGE_KEY.INSTAGRAM_MACHINE_CONTEXT,
  machineSignalStorageKey: STORAGE_KEY.INSTAGRAM_MACHINE_SIGNAL,
}

const linkedinMachineStorageKeys: PlatformAutomationMachineStorageKeys = {
  machineKeyStorageKey: STORAGE_KEY.LINKEDIN_MACHINE_KEY,
  machineOverlayStatusStorageKey: STORAGE_KEY.LINKEDIN_MACHINE_OVERLAY_STATUS,
  machineContextStorageKey: STORAGE_KEY.LINKEDIN_MACHINE_CONTEXT,
  machineSignalStorageKey: STORAGE_KEY.LINKEDIN_MACHINE_SIGNAL,
}

const redditMachineStorageKeys: PlatformAutomationMachineStorageKeys = {
  machineKeyStorageKey: STORAGE_KEY.REDDIT_MACHINE_KEY,
  machineOverlayStatusStorageKey: STORAGE_KEY.REDDIT_MACHINE_OVERLAY_STATUS,
  machineContextStorageKey: STORAGE_KEY.REDDIT_MACHINE_CONTEXT,
  machineSignalStorageKey: STORAGE_KEY.REDDIT_MACHINE_SIGNAL,
}

const snapchatMachineStorageKeys: PlatformAutomationMachineStorageKeys = {
  machineKeyStorageKey: STORAGE_KEY.SNAPCHAT_MACHINE_KEY,
  machineOverlayStatusStorageKey: STORAGE_KEY.SNAPCHAT_MACHINE_OVERLAY_STATUS,
  machineContextStorageKey: STORAGE_KEY.SNAPCHAT_MACHINE_CONTEXT,
  machineSignalStorageKey: STORAGE_KEY.SNAPCHAT_MACHINE_SIGNAL,
}

const stravaMachineStorageKeys: PlatformAutomationMachineStorageKeys = {
  machineKeyStorageKey: STORAGE_KEY.STRAVA_MACHINE_KEY,
  machineOverlayStatusStorageKey: STORAGE_KEY.STRAVA_MACHINE_OVERLAY_STATUS,
  machineContextStorageKey: STORAGE_KEY.STRAVA_MACHINE_CONTEXT,
  machineSignalStorageKey: STORAGE_KEY.STRAVA_MACHINE_SIGNAL,
}

const twitterMachineStorageKeys: PlatformAutomationMachineStorageKeys = {
  machineKeyStorageKey: STORAGE_KEY.TWITTER_MACHINE_KEY,
  machineOverlayStatusStorageKey: STORAGE_KEY.TWITTER_MACHINE_OVERLAY_STATUS,
  machineContextStorageKey: STORAGE_KEY.TWITTER_MACHINE_CONTEXT,
  machineSignalStorageKey: STORAGE_KEY.TWITTER_MACHINE_SIGNAL,
}

const tiktokMachineStorageKeys: PlatformAutomationMachineStorageKeys = {
  machineKeyStorageKey: STORAGE_KEY.TIKTOK_MACHINE_KEY,
  machineOverlayStatusStorageKey: STORAGE_KEY.TIKTOK_MACHINE_OVERLAY_STATUS,
  machineContextStorageKey: STORAGE_KEY.TIKTOK_MACHINE_CONTEXT,
  machineSignalStorageKey: STORAGE_KEY.TIKTOK_MACHINE_SIGNAL,
}

const venmoMachineStorageKeys: PlatformAutomationMachineStorageKeys = {
  machineKeyStorageKey: STORAGE_KEY.VENMO_MACHINE_KEY,
  machineOverlayStatusStorageKey: STORAGE_KEY.VENMO_MACHINE_OVERLAY_STATUS,
  machineContextStorageKey: STORAGE_KEY.VENMO_MACHINE_CONTEXT,
  machineSignalStorageKey: STORAGE_KEY.VENMO_MACHINE_SIGNAL,
}

const youtubeMachineStorageKeys: PlatformAutomationMachineStorageKeys = {
  machineKeyStorageKey: STORAGE_KEY.YOUTUBE_MACHINE_KEY,
  machineOverlayStatusStorageKey: STORAGE_KEY.YOUTUBE_MACHINE_OVERLAY_STATUS,
  machineContextStorageKey: STORAGE_KEY.YOUTUBE_MACHINE_CONTEXT,
  machineSignalStorageKey: STORAGE_KEY.YOUTUBE_MACHINE_SIGNAL,
}

const platformToStorageKeysMap: PlatformMachineStorageKeysMap = {
  [PLATFORM.BLUESKY]: blueskyMachineStorageKeys,
  [PLATFORM.FACEBOOK]: facebookMachineStorageKeys,
  [PLATFORM.GOOGLE]: googleMachineStorageKeys,
  [PLATFORM.INSTAGRAM]: instagramMachineStorageKeys,
  [PLATFORM.LINKEDIN]: linkedinMachineStorageKeys,
  [PLATFORM.REDDIT]: redditMachineStorageKeys,
  [PLATFORM.SNAPCHAT]: snapchatMachineStorageKeys,
  [PLATFORM.STRAVA]: stravaMachineStorageKeys,
  [PLATFORM.TIKTOK]: tiktokMachineStorageKeys,
  [PLATFORM.TWITTER]: twitterMachineStorageKeys,
  [PLATFORM.VENMO]: venmoMachineStorageKeys,
  [PLATFORM.YOUTUBE]: youtubeMachineStorageKeys,
}

// Local Storage Functions
export function getAutomationMachineStorageKey(platform: string) {
  return platformToStorageKeysMap[platform].machineKeyStorageKey
}

export function getAutomationMachineOverlayStorageKey(platform: string) {
  return platformToStorageKeysMap[platform].machineOverlayStatusStorageKey
}

export function getAutomationMachineContextStorageKey(platform: string) {
  const contextKey = state.constructMachineContextKey(platform)
  if (
    contextKey !== platformToStorageKeysMap[platform].machineContextStorageKey
  ) {
    _debug(
      `configured context key does not match ${contextKey}`,
      'automation stage machine',
      'd',
    )
  }
  return contextKey
}

function constructMachineInputDataKey(machineName) {
  return machineName + MACHINE_STORAGE_KEY_SUFFIX.INPUT_DATA
}

function constructMachineResumeDataKey(machineName) {
  return machineName + MACHINE_STORAGE_KEY_SUFFIX.RESUME_DATA
}

function constructMachinePersistentDataKey(machineName) {
  return machineName + MACHINE_STORAGE_KEY_SUFFIX.PERSISTENT_DATA
}

export function getAutomationMachineInputDataStorageKey(platform: string) {
  const inputDataKey = constructMachineInputDataKey(platform)
  return inputDataKey
}

export function getAutomationMachineResumeDataStorageKey(platform: string) {
  const resumeDataKey = constructMachineResumeDataKey(platform)
  return resumeDataKey
}

export function getAutomationMachinePersistentDataStorageKey(platform: string) {
  const stageDataKey = constructMachinePersistentDataKey(platform)
  return stageDataKey
}

export function getAutomationMachineSignalStorageKey(platform: string) {
  const signalKey = communication.constructMachineSignalKey(platform)
  if (
    signalKey !== platformToStorageKeysMap[platform].machineSignalStorageKey
  ) {
    _debug(
      `configured signal key does not match ${signalKey}`,
      'automation stage machine',
      'd',
    )
  }
  return signalKey
}

export function getStoredAutomationMachineKey(
  platform: string,
): Promise<string | null> {
  const machineKeyStorageKey = getAutomationMachineStorageKey(platform)

  return localStorage.getSingle(machineKeyStorageKey)
}

function setStoredAutomationMachineKey(
  platform: string,
  machineKey: string | null,
): Promise<void> {
  const machineKeyStorageKey = getAutomationMachineStorageKey(platform)

  return localStorage.set({
    [machineKeyStorageKey]: machineKey,
  })
}

export function setStoredMachineSignal(
  platform: string,
  signal: TriggeringEventType | null,
): Promise<void> {
  const machineSignalStorageKey = getAutomationMachineSignalStorageKey(platform)
  return localStorage.set({ [machineSignalStorageKey]: signal })
}

export function setStoredMachineOverlayStatus(
  platform: string,
  status: StageStatus,
): Promise<void> {
  const machineOverlayStatusStorageKey =
    getAutomationMachineOverlayStorageKey(platform)
  return localStorage.set({ [machineOverlayStatusStorageKey]: status })
}

export function getStoredMachineOverlayStatus(
  platform: string,
): Promise<StageStatus | null> {
  const machineOverlayStatusStorageKey =
    getAutomationMachineOverlayStorageKey(platform)

  return localStorage.getSingle(machineOverlayStatusStorageKey)
}

export function getStoredAutomationMachineInputData(
  platform: string,
): Promise<InputData> {
  const key = getAutomationMachineInputDataStorageKey(platform)
  return localStorage.getSingle(key) as Promise<InputData>
}

function setStoredAutomationMachineInputData(
  platform: string,
  data: any,
): Promise<void> {
  const key = getAutomationMachineInputDataStorageKey(platform)
  return localStorage.set({ [key]: data })
}

export function setStoredAutomationMachineResumeData(
  platform: string,
  data: ResumeDataBlob,
): Promise<void> {
  const key = getAutomationMachineResumeDataStorageKey(platform)
  return localStorage.set({ [key]: data })
}

export function setStoredAutomationMachinePersistentData(
  platform: string,
  data: any,
): Promise<void> {
  const key = getAutomationMachinePersistentDataStorageKey(platform)
  return localStorage.set({ [key]: data })
}

export async function automationStageMachineCleanup(platform) {
  await Promise.all([
    setStoredAutomationMachineKey(platform, null),
    setStoredMachineOverlayStatus(platform, null),
    setStoredAutomationMachineInputData(platform, null),
    setStoredAutomationMachineResumeData(platform, null),
    setStoredAutomationMachinePersistentData(platform, null),
    setStoredMachineSignal(platform, null),
    clearActiveTabId(platform),
    resetGlobalInterruptRequest(),
  ])
}

export async function multiPlatformMachineCleanUp() {
  const platforms = Object.keys(
    (await getStoredMultiPlatformRecommendationKeys()) ?? {},
  ) as PLATFORM[]
  // First, save the current agentic sidebar state
  await saveCurrentAgenticSidebarState()
  // Then clean up the multi platform machine
  await Promise.all([
    setStoredMultiPlatformMachineKey(null),
    setStoredMultiPlatformRemainingPlatforms(null),
    setStoredMultiPlatformRecommendationKeys(null),
    setStoredMultiPlatformTabId(null),
    setStoredMultiPlatformMachineOverlayStatus(null),
    ...platforms.map((platform) => automationStageMachineCleanup(platform)),
  ])
}

export async function multiPlatformMachinePostRun() {
  await Promise.all([
    setStoredMultiPlatformMachineKey(null),
    setStoredMultiPlatformRemainingPlatforms(null),
    updateStoredMultiPlatformMachineOverlayStatusTitle(
      'All done! — now back to your dashboard...',
    ),
    updateStoredMultiPlatformMachineOverlayStatusKeyRunningToPost(),
  ])
}

export async function multiPlatformMachinePreRun() {
  const currentAgenticSidebarState = await getStoredAgenticSidebarState()
  const platformStatuses = Object.fromEntries(
    Object.entries(currentAgenticSidebarState?.platformAccountIds ?? {}).map(
      ([platform, userId]) => [
        platform,
        { status: MultiPlatformMachinePlatformStatus.COMPLETED, userId },
      ],
    ),
  )
  await Promise.all([
    communication.sendSetMultiPlatformTabId(),
    setStoredMultiPlatformMachineOverlayStatus({
      title: '',
      multiPlatformStatusKey:
        currentAgenticSidebarState?.status ??
        MultiPlatformStatusKey.SELECT_PLATFORMS,
      platformStatuses,
    }),
  ])
}

export async function startMultiPlatformAutomationForRecommendations(
  platformRecommendations: Partial<Record<PLATFORM, RecommendationKeyEnum[]>>,
  automationType: AutomationType,
): Promise<void> {
  const platforms = Object.keys(platformRecommendations).sort() as PLATFORM[]
  if (platforms.length === 0) {
    return
  }
  // clean up any existing machines
  for (const platform of platforms) {
    await Promise.all([
      machineCleanUp(platform),
      automationStageMachineCleanup(platform),
      clearDeeplinkFeatureKey(platform),
    ])
  }

  // set the active tab for all platforms to this one
  await Promise.all(
    platforms.map(async (platform) => {
      communication.sendSetActiveTabForPlatform(platform)
    }),
  )
  await communication.sendSetMultiPlatformTabId()

  // navigate to first platform and set the machine key
  const firstPlatform = platforms[0]
  await setStoredMultiPlatformRemainingPlatforms(platforms)
  await setStoredMultiPlatformRecommendationKeys(platformRecommendations)
  await setStoredMultiPlatformMachineKey(
    `${MULTI_PLATFORM_MACHINE_KEY_PREFIX}${automationType}`,
  )
  await setStoredMultiPlatformMachineOverlayStatus({
    title: `Opening ${getPlatformName(firstPlatform)}...`,
    multiPlatformStatusKey:
      getMultiPlatformRunningStatusKeyForAutomationType(automationType),
    platformStatuses: platforms.reduce(
      (acc: MultiPlatformMachinePlatformStatuses, platform) => ({
        ...acc,
        [platform]: {
          status: MultiPlatformMachinePlatformStatus.PENDING,
        },
      }),
      {},
    ),
  })
  window.location.assign(getPlatformUrl(firstPlatform))
}

export async function startNextPlatformInMultiPlatformAutomation(
  platform: PLATFORM,
  platformUserId: string,
  automationType: AutomationType,
  inputData: InputData = {},
) {
  const newInputData: InputData = {
    ...inputData,
    platformUserId,
    isMultiPlatform: true,
    currentPlatform: platform,
    ifErrorGoToCleanup: true,
  }
  // Only add isRescan if the automation type is gather
  if (automationType === AutomationType.GATHER) {
    const isRescanAutomation = await isRescan(
      platform as PLATFORM,
      platformUserId,
    )

    newInputData.isRescan = isRescanAutomation
  }

  // remove this platform from the remaining platforms
  const multiPlatformPlatforms =
    await getStoredMultiPlatformRemainingPlatforms()

  const remainingPlatforms = multiPlatformPlatforms.slice(1)
  await setStoredMultiPlatformRemainingPlatforms(remainingPlatforms)
  newInputData.multiPlatformRemainingPlatforms = remainingPlatforms

  // set input data and update the multi platform machine key
  // This will cause a listener in the content script to load and run the machine
  await setStoredAutomationMachineInputData(platform, newInputData)
  await setStoredMultiPlatformMachineKey(
    `${MULTI_PLATFORM_MACHINE_KEY_PREFIX}${platform}___${automationType}`,
  )
}

export async function navigateToNextPlatform(
  multiPlatformPlatforms: PLATFORM[],
  googleAccountIndex?: number,
) {
  // Navigate to the next platform
  if (multiPlatformPlatforms && multiPlatformPlatforms.length > 0) {
    const nextPlatform = multiPlatformPlatforms[0]
    await updateStoredMultiPlatformMachineOverlayStatusTitle(
      `Done - Continuing to ${getPlatformName(nextPlatform)}...`,
    )
    _debug(
      `MULTI PLATFORM: navigating to ${nextPlatform}`,
      'finite stage machine',
    )
    if (nextPlatform === PLATFORM.GOOGLE || nextPlatform === PLATFORM.YOUTUBE) {
      // Google and YouTube are special cases - don't navigate to the login screen for these platforms
      if (googleAccountIndex) {
        window.location.assign(googleHomeUrl.format(googleAccountIndex))
      } else {
        window.location.assign(getPlatformUrl(nextPlatform))
      }
    } else {
      // For all other platforms, navigate to the login URL if it exists, otherwise the platform URL
      const loginUrl =
        getPlatformLoginUrl(nextPlatform) ?? getPlatformUrl(nextPlatform)
      window.location.assign(loginUrl)
    }
  } else {
    _debug('MULTI PLATFORM: no remaining platforms', 'finite stage machine')
    await multiPlatformMachinePostRun()
    if (canIUse.chromeRuntime()) {
      window.location.assign(chrome.runtime.getURL(extensionDashboardUrl))
    }
  }
}

export async function startAutomationMachineForRecommendation(
  platform: string,
  platformUserId: string,
  recommendationKey: string,
  automationType: AutomationType,
  inputData: InputData = {},
  isDeeplink = false,
): Promise<void> {
  // Clean up any existing machine data before starting new machine
  await Promise.all([
    machineCleanUp(platform),
    setStoredAutomationMachineKey(platform, null),
    setStoredMachineOverlayStatus(platform, null),
    setStoredAutomationMachineInputData(platform, null),
    setStoredAutomationMachineResumeData(platform, null),
    setStoredAutomationMachinePersistentData(platform, null),
    // Reset platform's overlay scroll position when starting an automation
    setOverlayScrollPosition(platform as PLATFORM, 0),
  ])

  const machineKey = `${recommendationKey}___${automationType}`

  if (isDeeplink) {
    await updateDeeplinkFeatureKey(
      platform as PLATFORM,
      recommendationKey as PLATFORM | RecommendationKeyEnum,
    )
  } else {
    await clearDeeplinkFeatureKey(platform as PLATFORM)
  }

  const newInputData: InputData = {
    ...inputData,
    platformUserId,
  }

  // Only add isRescan if the automation type is gather
  if (automationType === AutomationType.GATHER) {
    const isRescanAutomation = await isRescan(
      platform as PLATFORM,
      platformUserId,
    )

    newInputData.isRescan = isRescanAutomation
  }

  const machineStorageKey = getAutomationMachineStorageKey(platform)

  await localStorage.set({ [machineStorageKey]: machineKey })

  await setStoredAutomationMachineInputData(platform, newInputData)
}

export async function startBulkUpdateAutomationMachine(
  platform: PLATFORM,
  platformUserId: string,
  recommendationKeys: RecommendationKeyEnum[],
) {
  // Clean up any existing machine data before starting new machine
  await Promise.all([
    machineCleanUp(platform),
    setStoredAutomationMachineKey(platform, null),
    setStoredMachineOverlayStatus(platform, null),
    setStoredAutomationMachineInputData(platform, null),
    setStoredAutomationMachineResumeData(platform, null),
    setStoredAutomationMachinePersistentData(platform, null),
    // Reset platform's overlay scroll position when starting an automation
    setOverlayScrollPosition(platform as PLATFORM, 0),
  ])

  const machineKey = `${platform}___${AutomationType.UPDATE}`

  const newInputData: InputData = {
    platformUserId,
  }

  const machineStorageKey = getAutomationMachineStorageKey(platform)

  await setBatchUpdateRecommendationKeys(platform, recommendationKeys)

  await localStorage.set({ [machineStorageKey]: machineKey })

  await setStoredAutomationMachineInputData(platform, newInputData)
}

// Helper functions for constructing stages
function equalsCurrentUrl(url: string): boolean {
  const trailingSlashStrippedUrl = url.replace(/\/$/, '')
  const windowLocationHref = window.location.href.replace(/\/$/, '')
  return trailingSlashStrippedUrl === windowLocationHref
}

function matchesCurrentUrl(urlPatternRegExp: RegExp): boolean {
  const windowLocationHref = window.location.href.replace(/\/$/, '')
  return urlPatternRegExp.test(windowLocationHref)
}

// urlPattern strings must come from trusted sources and not user input
export function isUrlSet(
  url: string,
  urlPattern?: string | RegExp | undefined,
) {
  if (!urlPattern) {
    return equalsCurrentUrl(url)
  }
  const pattern =
    // eslint-disable-next-line security/detect-non-literal-regexp -- urlPattern is from internal stage defs, not user input
    urlPattern instanceof RegExp ? urlPattern : new RegExp(urlPattern)
  return matchesCurrentUrl(pattern)
}

function getDebugContext(
  machineContext: MachineContext,
  userContext: UserContext,
) {
  return {
    currentUrl: window.location.href,
    machineContext,
    platform: userContext.platform,
    userId: userContext.userId,
  }
}

// Functions for creating stages

function createActionStage(
  {
    type,
    key,
    status,
    url,
    urlPattern,
    action,
    waitMs,
    timeout,
    transitionNextStageMap,
  }: StageDef,
  onTriggeringEvent: OnTriggeringEventFn,
  defaultNextStageKey: string,
  defaultPausedStageKey: string,
  defaultErroredStageKey: string,
  defaultNeedsUserInputStageKey: string,
): Stage {
  const stage: Stage = {
    type,
    key,
    status,
    actions: {
      onEnter: async (machineContext, userContext, additionalMachineData) => {
        const {
          machineName: platform,
          currentStageKey,
          stageKeyHistoryStack,
          redirectRetryCount,
        } = machineContext
        remoteLog.info(`${platform}: ${currentStageKey}`)
        _debug(`stage.onEnter ${key}`, 'automation stage machine', 'd')
        _debug(
          { platform, currentStageKey, stageKeyHistoryStack },
          'automation stage machine',
          'd',
        )

        // If the stage requires being on a particular url, navigate
        // there now; when the page refreshes, the stage machine will
        // return to the top of this stage and the onEnter code will
        // run again
        if (url && !isUrlSet(url, urlPattern)) {
          _debug(
            `check if url correct (current: ${window.location.href}, ` +
              `expected: ${url}, ${urlPattern}`,
            'automation stage machine',
            'd',
          )
          if (redirectRetryCount < REDIRECT_RETRY_MAX) {
            await state.updateMachineContext(platform, {
              ...machineContext,
              redirectRetryCount: redirectRetryCount + 1,
            })
            await communication.changeUrlAndWait(url)
          } else {
            const redirectRetryMessage =
              `Redirect retry reached for ${url} ` +
              `and pattern ${urlPattern} at current url=${window.location.href}`

            remoteLog.debug(
              `${platform}: ${currentStageKey}: ${redirectRetryMessage}`,
            )
          }
        }
        _debug(`stage.onEnter URL Set ${url}`, 'automation stage machine', 'd')

        // Set the status for the overlay
        if (status) {
          setStoredMachineOverlayStatus(platform, status)
        }
        _debug(
          'stage.onEnter overlay status set',
          'automation stage machine',
          'd',
        )

        // Sometimes we need to slow down our automations to avoid
        // triggering platform-side rate limits
        if (waitMs !== null && waitMs !== undefined && waitMs > 0) {
          await new Promise((resolve) => {
            setTimeout(resolve, waitMs)
          })
        }

        const actionTimeout = timeout || DEFAULT_ACTION_STAGE_TIMEOUT

        try {
          _debug(
            'stage.onEnter checking interrupt',
            'automation stage machine',
            'd',
          )
          throwIfGlobalInterruptRequested()
          _debug(
            'stage.onEnter executing action',
            'automation stage machine',
            'd',
          )

          if (actionTimeout === NO_TIMEOUT) {
            await action(machineContext, userContext, additionalMachineData)
          } else {
            await Promise.race([
              throwIfTimeout(
                `Stage action not completed before timeout ${actionTimeout}ms.`,
                actionTimeout,
              ),
              action(machineContext, userContext, additionalMachineData),
            ])
          }
        } catch (error) {
          remoteLog.error(`automation stage machine: ${error.message}`)

          _debug(`caught exception ${error}`, 'automation stage machine', 'd')
          if (error instanceof GlobalInterrupt) {
            _debug(
              'return early Global Interrupt',
              'automation stage machine',
              'd',
            )
            return null
          }
          if (error instanceof NeedsUserInputError) {
            _debug(
              'transition to needs user input',
              'automation stage machine',
              'd',
            )

            return onTriggeringEvent(
              TriggeringEventType.ON_NEEDS_USER_INPUT,
              machineContext,
              userContext,
              additionalMachineData,
            )
          }
          _debug('transition to error', 'automation stage machine', 'd')

          // Log to Sentry, but change level depending on how stage is
          // configured to handle the error
          const nextStageOnError =
            transitionNextStageMap?.[TriggeringEventType.ON_ERROR]
          if (
            !nextStageOnError ||
            nextStageOnError === defaultErroredStageKey
          ) {
            logError(error, 'automation-stage-machine')
          } else {
            logInfo(error, 'automation-stage-machine')
          }

          communication.sendSetLastExtensionErrorStackTrace(error.stack)
          communication.sendSetLastFailedAutomationData(
            getDebugContext(machineContext, userContext),
          )

          return onTriggeringEvent(
            TriggeringEventType.ON_ERROR,
            machineContext,
            userContext,
            additionalMachineData,
          )
        }

        _debug(
          'stage.onEnter action completed',
          'automation stage machine',
          'd',
        )
        return onTriggeringEvent(
          TriggeringEventType.ON_EVENT_ACTION_COMPLETED,
          machineContext,
          userContext,
          additionalMachineData,
        )
      },
      onExit: async () => {},
      // removed params since currently unused
      // async (machineContext, userContext) => {}
    },
    transitions: {
      [TriggeringEventType.ON_EVENT_ACTION_COMPLETED]: {
        action: (additionalMachineData) => {
          additionalMachineData.resumeData.clear()
        },
        getTargetStageKey: () =>
          transitionNextStageMap?.[
            TriggeringEventType.ON_EVENT_ACTION_COMPLETED
          ] || defaultNextStageKey,
      },
      [TriggeringEventType.ON_ERROR]: {
        action: (additionalMachineData) => {
          additionalMachineData.resumeData.clear()
        },
        getTargetStageKey: () =>
          transitionNextStageMap?.[TriggeringEventType.ON_ERROR] ||
          defaultErroredStageKey,
      },
      [TriggeringEventType.ON_INPUT_PAUSE]: {
        action: () => {},
        getTargetStageKey: () => defaultPausedStageKey,
      },
      [TriggeringEventType.ON_INPUT_RESUME]: {
        action: () => {},
        getTargetStageKey: () => key,
      },
      [TriggeringEventType.ON_NEEDS_USER_INPUT]: {
        action: () => {},
        getTargetStageKey: () =>
          transitionNextStageMap?.[TriggeringEventType.ON_NEEDS_USER_INPUT] ||
          defaultNeedsUserInputStageKey,
      },
    },
  }

  return stage
}

export function createFinalStage(
  { type, key }: StageDef,
  finalCleanUpFn: () => Promise<void>,
): Stage {
  const stage: Stage = {
    actions: {
      onEnter: async (machineContext, userContext, additionalMachineData) => {
        const { googleAccountIndex } = userContext

        const {
          inputData: {
            closeTabOnCleanup,
            isMultiPlatform,
            multiPlatformRemainingPlatforms,
          },
          persistentData,
        } = additionalMachineData

        _debug('FINAL cleanup', 'finite stage machine')
        await finalCleanUpFn() // execute stage machine specific cleanup
        if (isMultiPlatform) {
          if (persistentData.data.multiPlatformSkipRemainingPlatforms) {
            _debug(
              'MULTI PLATFORM: skipping remaining platforms',
              'finite stage machine',
            )
            await multiPlatformMachinePostRun()
            if (canIUse.chromeRuntime()) {
              window.location.assign(
                chrome.runtime.getURL(extensionDashboardUrl),
              )
            }
          } else {
            _debug(
              'MULTI PLATFORM: navigating to next platform',
              'finite stage machine',
            )
            await navigateToNextPlatform(
              multiPlatformRemainingPlatforms,
              googleAccountIndex,
            )
          }
        } else if (closeTabOnCleanup) {
          _debug('closeTabOnCleanup set to TRUE', 'finite stage machine')
          await communication.sendCloseCurrentTab()
        }
      },
      onExit: async () => {},
    },
    key,
    type,
    transitions: {},
  }

  return stage
}

function createPausedStage(
  { type, key, status }: StageDef,
  cancelStageKey: string,
  multiPlatformCancelStageKey: string,
): Stage {
  const stage: Stage = {
    type,
    key,
    status,
    actions: {
      onEnter: async (machineContext, userContext) => {
        _debug('stage.onEnter for pause stage', 'automation stage machine', 'd')
        const { platform } = userContext
        if (status) {
          setStoredMachineOverlayStatus(platform, status)
        }
      },
      onExit: async () => {
        _debug('exiting pause', 'automation stage machine', 'd')
      },
    },
    transitions: {
      [TriggeringEventType.ON_INPUT_RESUME]: {
        getTargetStageKey: (machineContext: MachineContext) => {
          const { stageKeyHistoryStack } = machineContext
          stageKeyHistoryStack.pop() // current stage
          const lastStageKey = stageKeyHistoryStack.pop()
          return lastStageKey
        },
      },
      [TriggeringEventType.ON_INPUT_CANCEL]: {
        getTargetStageKey: () => cancelStageKey,
      },
      [TriggeringEventType.ON_MULTI_PLATFORM_CANCEL]: {
        getTargetStageKey: () => multiPlatformCancelStageKey,
      },
    },
  }

  return stage
}

function createNeedsUserInputStage(
  { type, key, status }: StageDef,
  cancelStageKey: string,
  pausedStageKey: string,
): Stage {
  const stage: Stage = {
    type,
    key,
    status,
    actions: {
      onEnter: async (machineContext, userContext) => {
        _debug(
          'stage.onEnter for needs user input stage',
          'automation stage machine',
          'd',
        )
        const { platform } = userContext
        if (status) {
          setStoredMachineOverlayStatus(platform, status)
        }
      },
      onExit: async () => {
        _debug('exiting needs user input', 'automation stage machine', 'd')
      },
    },
    transitions: {
      [TriggeringEventType.ON_INPUT_RESUME]: {
        getTargetStageKey: (machineContext: MachineContext) => {
          const { stageKeyHistoryStack } = machineContext
          stageKeyHistoryStack.pop() // current stage
          const lastStageKey = stageKeyHistoryStack.pop()
          return lastStageKey
        },
      },
      [TriggeringEventType.ON_INPUT_PAUSE]: {
        getTargetStageKey: () => pausedStageKey,
      },
      [TriggeringEventType.ON_INPUT_CANCEL]: {
        getTargetStageKey: () => cancelStageKey,
      },
      [TriggeringEventType.ON_MULTI_PLATFORM_SKIP_PLATFORM]: {
        action: async (additionalMachineData) => {
          const {
            inputData: { currentPlatform },
          } = additionalMachineData
          await updateStoredMultiPlatformMachineOverlayPlatformStatus(
            currentPlatform,
            { status: MultiPlatformMachinePlatformStatus.SKIPPED },
          )
        },
        getTargetStageKey: () => cancelStageKey,
      },
    },
  }

  return stage
}

export function createMultiPlatformCancelStage(
  { type, key }: StageDef,
  finalStageKey: string,
  onTriggeringEvent: OnTriggeringEventFn,
): Stage {
  const stage: Stage = {
    type,
    key,
    actions: {
      onEnter: async (machineContext, userContext, additionalMachineData) => {
        const {
          persistentData,
          inputData: { multiPlatformRemainingPlatforms },
        } = additionalMachineData
        const { platform } = userContext
        _debug('MULTI PLATFORM CANCEL', 'finite stage machine')
        await persistentData.set({
          ...persistentData.data,
          multiPlatformSkipRemainingPlatforms: true,
        })
        await updateStoredMultiPlatformMachineOverlayPlatformStatus(
          [...multiPlatformRemainingPlatforms, platform],
          { status: MultiPlatformMachinePlatformStatus.SKIPPED },
        )
        return onTriggeringEvent(
          TriggeringEventType.ON_EVENT_ACTION_COMPLETED,
          machineContext,
          userContext,
          additionalMachineData,
        )
      },
      onExit: async () => {
        _debug('exiting multi platform cancel', 'automation stage machine', 'd')
      },
    },
    transitions: {
      [TriggeringEventType.ON_EVENT_ACTION_COMPLETED]: {
        getTargetStageKey: () => finalStageKey,
      },
    },
  }
  return stage
}

export function createErroredStage(
  { type, key, status }: StageDef,
  onTriggeringEvent: (
    trigger: TriggeringEventType,
    machineContext: MachineContext,
    userContext: UserContext,
    additionalMachineData: AdditionalMachineData,
  ) => Promise<void>,
  cancelStageKey: string,
): Stage {
  const stage: Stage = {
    type,
    key,
    status,
    actions: {
      onEnter: async (machineContext, userContext, additionalMachineData) => {
        const { platform } = userContext
        const { inputData, persistentData } = additionalMachineData
        _debug('stage.onEnter for error stage', 'automation stage machine', 'd')

        // Get recommendation specific error status if exists
        const lastErroredRecommendationKey =
          persistentData.data.lastErroredRecommendationKey || undefined
        _debug(lastErroredRecommendationKey, 'stage.onEnter errored keys', 'd')

        let liveErrorStatusByKey = null
        if (lastErroredRecommendationKey) {
          liveErrorStatusByKey =
            await communication.sendGetRecommendationErrorStatus(
              lastErroredRecommendationKey,
            )
          _debug(
            liveErrorStatusByKey,
            'stage.onEnter fetched live error status',
            'd',
          )
        }

        // Check for known error types
        let errorStatusByType: StageStatus

        const errorName = persistentData.data.errorName
        if (errorName === FACEBOOK_RATE_LIMIT_EXCEPTION_NAME) {
          errorStatusByType = {
            title: 'Rate limit reached',
            body:
              'We cannot continue this task right now, but you should ' +
              'be able to resume in 24 hours when the rate limit resets',
            stageKey: ControlStageKey.ERRORED,
          }
        } else if (errorName === FACEBOOK_PASSWORD_PROMPT_EXCEPTION_NAME) {
          errorStatusByType = {
            title: 'Facebook needs you to enter your password',
            body:
              'We were interrupted by Facebook asking for your password. ' +
              'Please close this error message, enter your password and run the automation again. ' +
              `Don't worry, ${BuildConfig.TENANT_NAME} can't see your password.`,
            stageKey: ControlStageKey.ERRORED,
          }
        } else if (
          errorName === LinkedinShowProfilePhotoErrorName.NO_PROFILE_PHOTO
        ) {
          errorStatusByType = {
            title: 'Could not update profile picture visibility',
            body: 'We could not find the setting for updating profile picture visibility. Is it possible you no longer have a profile picture?',
            stageKey: ControlStageKey.ERRORED,
          }
        }

        setStoredMachineOverlayStatus(
          platform,
          liveErrorStatusByKey || errorStatusByType || status,
        )

        if (inputData.isMultiPlatform) {
          await updateStoredMultiPlatformMachineOverlayPlatformStatus(
            platform as PLATFORM,
            { status: MultiPlatformMachinePlatformStatus.ERRORED },
          )
        }

        if (inputData.ifErrorGoToCleanup) {
          return onTriggeringEvent(
            TriggeringEventType.ON_SKIP_ERROR,
            machineContext,
            userContext,
            additionalMachineData,
          )
        }

        return null
      },
      onExit: async (machineContext, userContext, additionalMachineData) => {
        _debug('exiting error', 'automation stage machine', 'd')
        const { platform } = userContext
        const { persistentData } = additionalMachineData

        await persistentData.clear()
        setStoredMachineOverlayStatus(platform, null)
      },
    },
    transitions: {
      [TriggeringEventType.ON_INPUT_CANCEL]: {
        getTargetStageKey: () => cancelStageKey,
      },
      [TriggeringEventType.ON_INPUT_RETRY]: {
        action: () => {},
        getTargetStageKey: (machineContext: MachineContext) => {
          const { stageKeyHistoryStack } = machineContext
          return stageKeyHistoryStack[0]
        },
      },
      [TriggeringEventType.ON_SKIP_ERROR]: {
        // If "on skip error", go straight to cancellation
        getTargetStageKey: () => cancelStageKey,
      },
    },
  }
  return stage
}

function constructAutomationSequenceStagesAndRegistry(
  { defaultSequenceStageDefs, controlFlowStageDefs }: AutomationSequenceDefs,
  onTriggeringEvent: OnTriggeringEventFn,
  finalCleanUpFn: () => Promise<void>,
): StageRegistry {
  const stageRegistry = {}

  let pausedStageKey: string
  let cancelStageKey: string
  let erroredStageKey: string
  let finalStageKey: string
  let needsUserInputStageKey: string
  let multiPlatformCancelStageKey: string
  for (const stageDef of controlFlowStageDefs) {
    const { key } = stageDef
    if (key === ControlStageKey.PAUSED) {
      pausedStageKey = key
    } else if (key === ControlStageKey.CANCEL) {
      cancelStageKey = key
    } else if (key === ControlStageKey.ERRORED) {
      erroredStageKey = key
    } else if (key === ControlStageKey.FINAL) {
      finalStageKey = key
    } else if (key === ControlStageKey.NEEDS_USER_INPUT) {
      needsUserInputStageKey = key
    } else if (key === ControlStageKey.MULTI_PLATFORM_CANCEL) {
      multiPlatformCancelStageKey = key
    }
  }

  for (const stageDef of controlFlowStageDefs) {
    const { type, key } = stageDef
    if (key in stageRegistry) {
      throw new Error(`Key conflict in stage registry for ${key}`)
    }

    // Construct control stage based on type
    let stage: Stage
    if (type === StageType.PAUSED) {
      stage = createPausedStage(
        stageDef,
        cancelStageKey,
        multiPlatformCancelStageKey,
      )
    } else if (type === StageType.CANCEL) {
      stage = createActionStage(
        stageDef,
        onTriggeringEvent,
        finalStageKey,
        pausedStageKey,
        erroredStageKey,
        needsUserInputStageKey,
      )
    } else if (type === StageType.ERRORED) {
      stage = createErroredStage(stageDef, onTriggeringEvent, cancelStageKey)
    } else if (type === StageType.FINAL) {
      stage = createFinalStage(stageDef, finalCleanUpFn)
    } else if (type === StageType.NEEDS_USER_INPUT) {
      stage = createNeedsUserInputStage(
        stageDef,
        cancelStageKey,
        pausedStageKey,
      )
    } else if (type === StageType.MULTI_PLATFORM_CANCEL) {
      stage = createMultiPlatformCancelStage(
        stageDef,
        finalStageKey,
        onTriggeringEvent,
      )
    }

    // Add stage to registry
    stageRegistry[key] = stage
  }

  for (const [index, stageDef] of defaultSequenceStageDefs.entries()) {
    const { type, key } = stageDef
    if (key in stageRegistry) {
      throw new Error(`Key conflict in stage registry for ${key}`)
    }

    // Create action stages
    let stage: Stage
    if (type === StageType.ACTION) {
      const defaultNextStageKey =
        index < defaultSequenceStageDefs.length - 1
          ? defaultSequenceStageDefs[index + 1].key
          : finalStageKey
      stage = createActionStage(
        stageDef,
        onTriggeringEvent,
        defaultNextStageKey,
        pausedStageKey,
        erroredStageKey,
        needsUserInputStageKey,
      )
    }

    // Add stage to registry
    stageRegistry[key] = stage
  }

  return stageRegistry
}

// Create automation sequence stage machine
// A specialized machine that has a default sequence that runs
// straight through in a happy case scenario, with other control flow
// stages for handling things like errors and user input
export function createAutomationSequenceMachine(
  { machineName, initialStageKey, stageDefs }: AutomationStageMachineDef,
  isMultiPlatform: boolean = false,
): Machine {
  const createStagesAndRegistryFn = (onTriggeringEvent) =>
    constructAutomationSequenceStagesAndRegistry(
      stageDefs,
      onTriggeringEvent,
      () => automationStageMachineCleanup(machineName),
    )
  return createStageMachine(
    {
      machineName,
      initialStageKey,
      createStagesAndRegistryFn,
    },
    isMultiPlatform,
  )
}
